import React from 'react';
import UserImageCircle from "../../components/UserImageCircle";
import { useCandidate } from "../../providers/CandidateProvider";
import { RoleMatchBreakdown } from "../profiles/sections/hire-confidence/HireConfidenceBreakdowns";
import { COMPONENT_NOTES, themeFor } from "../profiles/sections/hire-confidence/HireConfidenceParts";

/**
 * Where Role Match came from, as a modal off the candidate tile.
 *
 * The body is not a second rendering of the score: it is the same
 * `RoleMatchBreakdown` the profile's Hire Confidence accordion expands, from
 * the same file. Two renderings of one calculation drift apart the first time
 * either is touched, and a reviewer comparing the tile against the profile
 * would have no way to tell which of them was right. Only the header differs,
 * because the two surfaces frame the same number differently — see below.
 */
const CandidateRoleMatchDetails = ({ candidate: candidateProp, criteria = {} }) => {
    // Modal content mounts outside the tile's provider tree, so the candidate
    // normally arrives as a prop; the context lookup only covers inline callers.
    const context = useCandidate();
    const candidate = candidateProp || context?.candidate;

    const component = TB.find(
        candidate?.hireConfidenceComponents?.() || [],
        ['key', 'roleMatch']
    );

    if (!component) {
        return (
            <div className="p-6 text-center">
                <p className="m-0 text-base">
                    This talent hasn't been scored against a role yet.
                </p>
            </div>
        );
    }

    // Same guard the profile section uses: the helper reads straight into
    // `matching` and throws for a talent who has none.
    const metrics = candidate.hasMatching?.()
        ? (candidate.matchScoresForCriteria?.(criteria) || {})
        : {};

    const theme = themeFor(component.key);

    return (
        <div className="candidate-role-match-details flex flex-col">

            {/* Who and what, on one line.

                The modal opens from a grid of tiles, so it has to say whose
                score this is — but the weight and the earned figure the
                accordion row carries are dropped here. On the profile those
                exist to show this row's share of a total sitting directly above
                it; with no total on screen, "45% of total" is a fraction of
                nothing, and the vertical space it cost pushed the two halves
                that are the actual point below the fold.

                Tinted like the row it mirrors: the colour ties this back to the
                Role Match slice of Hire Confidence rather than to the
                client-facing Match Score, which is a different number built
                from only half of what's below. */}
            <div className={`flex flex-row flex-wrap items-center gap-x-6 gap-y-3 border-b border-[#EDEEF2] px-5 py-4 sm:px-7 ${theme.soft}`}>
                <div className="flex min-w-0 flex-1 flex-row items-center gap-3">
                    <UserImageCircle
                        user={candidate}
                        hideTooltip={true}
                        size={48}
                        className="img-grayscale"
                    />

                    <div className="min-w-0">
                        <h5 className="mb-0 truncate">
                            {candidate.firstNamePlusInitial?.() || candidate.first_name}
                        </h5>
                        <p className="mb-0 truncate text-sm text-gray-500">
                            {candidate.job_title}
                        </p>
                    </div>
                </div>

                <div className="flex shrink-0 flex-row items-center gap-3">
                    <span
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-base text-white"
                        style={{ backgroundColor: theme.hex }}
                    >
                        <i className={`fas ${theme.icon}`} />
                    </span>

                    <div className="min-w-0 max-w-[280px]">
                        <p className="mb-0 text-lg font-bold tracking-[-0.01em] text-[#0E0E14]">
                            {component.label}
                        </p>
                        <p className="mb-0 mt-0.5 text-xs leading-[1.45] text-[#8B8F9A]">
                            {COMPONENT_NOTES[component.key]}
                        </p>
                    </div>
                </div>
            </div>

            {/* `isOpen` is what animates the two rings up from zero; the
                accordion toggles it to replay them, a modal is only ever open. */}
            <div className="px-5 py-5 sm:px-7">
                <RoleMatchBreakdown
                    talent={candidate}
                    metrics={metrics}
                    isOpen
                />
            </div>
        </div>
    );
};

export default CandidateRoleMatchDetails;
